import { addLastSlash, normalizeUrlPathname } from '@/shared/modules/api';

import { LAYOUT_SEGMENTS } from './constants/layout-segments';

export const isPathnameRelative = (pathname: string) => {
  return typeof pathname === 'string' && pathname.startsWith('/');
};

/**
 * @note
 * Check URL origin on the client
 */
export const doesUrlHaveSameOrigin = (url: URL | string) => {
  const stringUrl = url.toString();

  if (isPathnameRelative(stringUrl)) {
    return true;
  }

  if (stringUrl.startsWith('http') && typeof window !== 'undefined') {
    return new URL(stringUrl).origin === new URL(window.location.href).origin;
  }

  return false;
};

/**
 * Returns a map of methods, each of which can return a relative pathname to a specific route segment based on the
 * provided parameters.
 * @example
 * ```
 *   const locale = 'uk';
 *   const pathname = getRelativePathnameTo(locale).gameplaySegment('a-favorite-game');
 *   //    ^^^ will be equal to the string: '/uk/game/a-favorite-game/play/'
 * ```
 */
export const getRelativePathnameTo = (locale: string) => {
  const normalizeSegments = (segments: string[]) => addLastSlash(normalizeUrlPathname(segments.join('/')));

  const rootSegment = () => {
    return normalizeSegments([locale, LAYOUT_SEGMENTS.ROOT]);
  };

  const authSegment = () => {
    return normalizeSegments([locale, LAYOUT_SEGMENTS.AUTH]);
  };

  const downloadSegment = () => {
    return normalizeSegments([locale, LAYOUT_SEGMENTS.DOWNLOAD]);
  };

  const gameSegment = () => {
    return normalizeSegments([locale, LAYOUT_SEGMENTS.GAME]);
  };

  const gameplaySegment = (game: string) => {
    return normalizeSegments([locale, LAYOUT_SEGMENTS.GAME, game, LAYOUT_SEGMENTS.GAME_PLAY]);
  };

  const profileSegment = (userId: string = '') => {
    return normalizeSegments([locale, LAYOUT_SEGMENTS.PROFILE, userId]);
  };
  const profileFriendsSegment = (userId: string) => {
    return normalizeSegments([locale, LAYOUT_SEGMENTS.PROFILE, userId, LAYOUT_SEGMENTS.PROFILE_FRIENDS]);
  };
  const profileLibrarySegment = (userId: string) => {
    return normalizeSegments([locale, LAYOUT_SEGMENTS.PROFILE, userId, LAYOUT_SEGMENTS.PROFILE_LIBRARY]);
  };

  const redeemGamePromoCodesSegment = (game: string) => {
    return normalizeSegments([locale, LAYOUT_SEGMENTS.REDEEM_GAME_PROMO_CODES, game]);
  };

  const referralGameDownloadSegment = (game: string) => {
    return normalizeSegments([locale, LAYOUT_SEGMENTS.REFERRAL_GAME_DOWNLOAD, game]);
  };

  const anySegment = (path: string[]) => {
    return normalizeSegments([locale, ...path]);
  };

  return {
    anySegment,
    rootSegment,
    authSegment,
    gameSegment,
    gameplaySegment,
    downloadSegment,
    profileSegment,
    profileFriendsSegment,
    profileLibrarySegment,
    redeemGamePromoCodesSegment,
    referralGameDownloadSegment,
  };
};
